import clsx from 'clsx';
import { HTMLAttributes, ReactNode, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { ThemeProps } from '../@types/theme-props';
import CurrentTheme from './current-theme';
import ThemeContext, { ThemeContextProps } from './theme-context';

interface ThemeComponentProps extends HTMLAttributes<HTMLOrSVGElement> {
  /** The theme to apply. */
  theme: ThemeProps;
  children: ReactNode;
  /** Classes to apply to the wrapper div. */
  className?: string;
  /** The tag to render, defaults to a div. */
  tag?: keyof JSX.IntrinsicElements;
}

const changeThemeCallback = (setThemeContext, theme) => {
  setThemeContext((themeContext) => ({ ...themeContext, theme: new CurrentTheme(theme) }));
};

function useThemeContextState(theme: ThemeProps) {
  const [themeContext, setThemeContext] = useState<ThemeContextProps>({
    theme: new CurrentTheme(theme),
    changeTheme: (newTheme: ThemeProps) => changeThemeCallback(setThemeContext, newTheme),
  });

  // if the item props change for any reason, we need to update the context and re-render
  useUpdateEffect(() => changeThemeCallback(setThemeContext, theme), [theme]);

  return themeContext;
}

/**
 * Applies the given theme, including setting the background and text, and setting up
 * a ThemeContext for descendant components.
 */
function Theme({ theme, children, className, tag: Tag = 'div', ...rest }: ThemeComponentProps) {
  const themeContext = useThemeContextState(theme);

  const classes = clsx({
    'bg-background': themeContext.theme.backgroundColor,
    'text-text': themeContext.theme.textColor,
    'transition-colors': true,
    [className]: !!className,
  });

  return (
    <ThemeContext.Provider value={themeContext}>
      <Tag className={classes} style={themeContext.theme.themeStyles} {...rest}>
        {children}
      </Tag>
    </ThemeContext.Provider>
  );
}

export default Theme;
